import { Box, GlobalStyles } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const CHIP_WIDTH = 16;
const CHIP_HEIGHT = 40;

export const SIDEBAR_WIDTH_BOUNDS = { min: 280, max: 800 } as const;
export const DEFAULT_SIDEBAR_WIDTH = 380;

interface ResizableSidebarProps {
  children: ReactNode;
  width: number;
  onWidthChange: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
}

export const ResizableSidebar: FC<ResizableSidebarProps> = ({
  children,
  width,
  onWidthChange,
  minWidth = SIDEBAR_WIDTH_BOUNDS.min,
  maxWidth = SIDEBAR_WIDTH_BOUNDS.max,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [chipPosition, setChipPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const handleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const updateChipPosition = useCallback(() => {
    const el = handleRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setChipPosition({
      left: rect.left - CHIP_WIDTH / 2,
      top: rect.top + rect.height / 2 - CHIP_HEIGHT / 2,
    });
  }, []);

  useEffect(() => {
    updateChipPosition();
    const observer = new ResizeObserver(updateChipPosition);
    const el = handleRef.current;
    if (el) observer.observe(el);
    window.addEventListener('scroll', updateChipPosition, true);
    window.addEventListener('resize', updateChipPosition);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateChipPosition, true);
      window.removeEventListener('resize', updateChipPosition);
    };
  }, [updateChipPosition]);

  useEffect(() => {
    const t = setTimeout(updateChipPosition, 0);
    return () => clearTimeout(t);
  }, [width, updateChipPosition]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      /* eslint-disable immutable/no-mutation -- refs hold drag session state */
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      /* eslint-enable immutable/no-mutation */
      setIsDragging(true);
    },
    [width],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const delta = startXRef.current - e.clientX;
      const next = Math.min(
        maxWidth,
        Math.max(minWidth, startWidthRef.current + delta),
      );
      onWidthChange(next);
    },
    [minWidth, maxWidth, onWidthChange],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const chip = chipPosition && (
    <Box
      sx={{
        position: 'fixed',
        left: chipPosition.left,
        top: chipPosition.top,
        width: CHIP_WIDTH,
        height: CHIP_HEIGHT,
        borderRadius: CHIP_HEIGHT / 2,
        bgcolor: 'white',
        border: '1px solid',
        borderColor: 'grey.300',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)',
        zIndex: 2,
        transform: isHovered ? 'scale(1.12)' : 'none',
        transition: 'transform 0.15s ease-out',
      }}
    >
      <DragIndicator
        sx={{
          color: 'grey.500',
          fontSize: 14,
          transform: 'rotate(90deg)',
          opacity: 0.85,
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      {isDragging && (
        <GlobalStyles
          styles={{
            body: { cursor: 'col-resize', userSelect: 'none' },
          }}
        />
      )}
      <Box
        ref={handleRef}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'col-resize',
          zIndex: 1,
          borderLeft: '1px solid',
          borderColor: isHovered ? 'primary.main' : 'divider',
          boxSizing: 'border-box',
          transition: 'border-color 0.15s ease-out',
        }}
      />
      {isMounted && chip && createPortal(chip, document.body)}
      <Box
        sx={{
          pl: 4,
          pt: 1,
          pb: 2,
          height: '100%',
          overflow: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
